import { Component, OnInit, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { BaseModel } from '@app/shared/models/base';
import { ConfirmModalComponent } from '@app/shared/dialogs/confirm-dialog/confirm-modal.component';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import * as _ from 'lodash-es';
import { CdkDragDrop, DragAxis, moveItemInArray } from '@angular/cdk/drag-drop';


export interface ActionListItem {
  actionListDisplayName: string;
}

export interface ActionListBtnConfig<T extends BaseModel & ActionListItem> {
  option?: ActionListOption | string; // Either an ActionListOption or a translated string
  label?: string;
  action?: (item: T) => any;
  show?: (item: T) => boolean;
  order: number;
}

export enum ActionListOption {
  COPY = 'COPY',
  DELETE = 'DELETE'
}

@Component({
  selector: 'action-list',
  templateUrl: './action-list.component.html',
  styleUrls: ['./action-list.component.scss']
})
export class ActionListComponent<T extends BaseModel & ActionListItem> extends BaseWithBundleComponent
                                                                       implements OnInit {

  @Input()
  public items: T[];

  @Input()
  public selected: T;

  @Input()
  public identifier?: string;

  @Input()
  public options?: ActionListBtnConfig<T>[];

  @Input()
  public template?: TemplateRef<any>;

  @Input()
  public addOn?: TemplateRef<any>;

  @Input()
  public reorderable?: boolean;

  @Output()
  public reordered = new EventEmitter<any>();

  @Output()
  public select = new EventEmitter<T>();

  @Output()
  public copy = new EventEmitter<T>();

  @Output()
  public delete = new EventEmitter<T>();

  public lockAxis: DragAxis;

  get hasMenu(): boolean {
    return this.options && this.options.length > 0;
  }

  constructor(protected componentServiceBundle: ComponentServiceBundle) {
    super(componentServiceBundle);
    this._idPrefix = 'action_list.';
  }

  public ngOnInit() {
    super.ngOnInit();
    this.setOptions();

    if (this.reorderable) {
      this.lockAxis = 'y';
    }
  }

  private findAndConstructOption(option: ActionListOption, label?: string, action?: (item: T) => any): ActionListBtnConfig<T> {
    const found = this.options && this.options.find(btnConfig => btnConfig.option === option);

    // If there is no action linked yet, we link the default action provided by the param
    if (found && !found.action) {
      found.action = action ? action : this.noop;
    }

    if (found && !found.label && label) {
      found.label = label;
    }

    return found;
  }

  private setOptions(): void {
    if (this.options) {
      this.options = _.sortBy(this.options, (btnConfig: ActionListBtnConfig<T>) => btnConfig.order);

      this.findAndConstructOption(ActionListOption.COPY, marker('common.btn.copy'), (item: T) => {
        this.copyItem(item);
      });

      this.findAndConstructOption(ActionListOption.DELETE, marker('common.btn.delete'), (item: T) => {
        this.deleteItem(item);
      });

      this.options.forEach(option => {
        if (!option.action) {
          option.action = this.noop;
        }
      });
    }
  }

  public noop(item?: T): void {
    console.log('No method linked to action list button.');
  }

  public selectItem(item: T): void {
    this.select.emit(item);
  }

  public copyItem(item: T): void {
    this.copy.emit(item);
  }

  public deleteItem(item: T): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '400px',
      data: {
        title: this.translateService.instant(marker('common.dialog.delete.title')),
        text: `${this.translateService.instant(marker('common.dialog.delete.text'), { name: item.actionListDisplayName})}`
      }
    });

    dialogRef.afterClosed()
        .subscribe((result: boolean) => {
          if (result) {
            this.delete.emit(item);
          }
        });
  }

  public setNewOrder(dragDrop: CdkDragDrop<any>): void {
    if (dragDrop.previousContainer === dragDrop.container) {
      moveItemInArray(this.items, dragDrop.previousIndex, dragDrop.currentIndex);
      this.reordered.emit();
    }
  }

}
